/**
 * GA4 Variant SKU → Item ID lookup map (sessionStorage)
 *
 * Populated by fireAddToCart (assets/js/theme/custom/event/ga4-events.js) and
 * cleared by the cart-remove handlers when the cart becomes empty.
 *
 * Purpose: Checkout-stage inline scripts (order-confirmation.html, the
 * empty.html checkout container) have access to variant SKUs but not the
 * product-level (mothership) SKU that PDP/listing GA4 events use as item_id.
 * Reading this map lets them recover item_id, restoring consistent GA4 item
 * identity across the funnel.
 *
 * Storage key: 'ga4_variant_sku_map'
 * Storage shape: JSON.stringify({ [variantSku]: itemId, ... })
 *
 * Inline read pattern for non-bundled checkout pages (no webpack import
 * available — paste this snippet directly into the inline <script>):
 *
 *     function getItemIdForVariantInline(variantSku) {
 *         try {
 *             const map = JSON.parse(sessionStorage.getItem('ga4_variant_sku_map')) || {};
 *             return map[variantSku] || variantSku;
 *         } catch (_) {
 *             return variantSku;
 *         }
 *     }
 */

const STORAGE_KEY = 'ga4_variant_sku_map';

function readMap() {
    try {
        if (typeof sessionStorage === 'undefined') return {};
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (_) {
        return {};
    }
}

function writeMap(map) {
    try {
        if (typeof sessionStorage === 'undefined') return;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch (_) {
        /* storage unavailable / quota exceeded — degrade silently */
    }
}

/**
 * Record a variantSku → itemId entry. Skips identity entries and empty args.
 * @param {string} variantSku
 * @param {string} itemId
 */
export function setItemIdForVariant(variantSku, itemId) {
    if (!variantSku || !itemId) return;
    if (variantSku === itemId) return;

    const map = readMap();
    if (map[variantSku] === itemId) return;
    map[variantSku] = itemId;
    writeMap(map);
}

/**
 * Look up the item_id for a given variantSku. Falls back to variantSku itself
 * when no entry exists or storage is unavailable.
 * @param {string} variantSku
 * @returns {string}
 */
export function getItemIdForVariant(variantSku) {
    const map = readMap();
    return map[variantSku] || variantSku;
}

/**
 * Remove the entire map. Called by cart-remove handlers when the resulting
 * cart is empty.
 */
export function clearVariantSkuMap() {
    try {
        if (typeof sessionStorage === 'undefined') return;
        sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {
        /* storage unavailable — degrade silently */
    }
}

/**
 * Decorate a cart-items array in place with the fields mapCartItemToGa4Item reads:
 *   item.variant_sku — the variant SKU (mirrors item.sku)
 *   item.product_sku — the mothership SKU from ga4_variant_sku_map, falling back
 *                      to the variant SKU when no entry exists.
 *
 * Preserves any pre-set values via `||` so enriched contexts are not clobbered.
 * No-op on non-array input or items without a `.sku`.
 *
 * @param {Array} items
 * @returns {Array} the same array, for chainability
 */
export function decorateCartItemsWithSkus(items) {
    if (!Array.isArray(items)) return items;
    for (const item of items) {
        if (!item || !item.sku) continue;
        item.variant_sku = item.variant_sku || item.sku;
        item.product_sku = item.product_sku || getItemIdForVariant(item.sku);
    }
    return items;
}
